import { Link } from 'react-router-dom';
import {
  UXP_RI_ID,
  COMMAND_CENTER_METRICS,
  BUSINESS_GOALS,
  CAMPAIGN_TYPES,
  CAMPAIGN_BUILDER_FEATURES,
  KEYWORD_TYPES,
  BID_BUDGET_FEATURES,
  ORGANIC_PAID_DIMENSIONS,
  OPTIMIZATION_RECS,
  COMPETITOR_DIMENSIONS,
  FORECAST_METRICS,
  EXPERIMENT_TYPES,
  COLLABORATION_FEATURES,
  AI_LEARNING_SOURCES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_CAMPAIGNS,
  DEMO_KEYWORDS,
  DEMO_ORGANIC_PAID,
  DEMO_FORECASTS,
  DEMO_OPTIMIZATIONS,
  buildRevenueIntelDashboard,
} from '../../revenueintel/framework';
import {
  BusinessCommandCenterPanel,
  BusinessGoalsGrid,
  CampaignManagementPanel,
  KeywordIntelligenceCard,
  BidBudgetEnginePanel,
  OrganicPaidCorrelationPanel,
  ForecastingPanel,
  AIOptimizationPanel,
} from '../../components/revenueintel';
import { Card, KpiCard } from '../../components/ui';

export function RevenueIntelligencePage() {
  const dashboard = buildRevenueIntelDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_RI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Revenue Intelligence Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          AI Advertising, Revenue Intelligence & Growth Operating System — business outcomes over isolated ad metrics.
          Orchestrates advertising, organic performance, creative assets, inventory, and financial outcomes.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Business Goals" value={dashboard.business_goals.toString()} />
        <KpiCard label="Campaign Types" value={dashboard.campaign_types.toString()} />
        <KpiCard label="Forecast Metrics" value={dashboard.forecast_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Business Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BusinessCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Business Goals</h2>
        <div className="mt-4">
          <BusinessGoalsGrid goals={BUSINESS_GOALS} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Campaign Management</h2>
          <p className="mt-1 text-sm text-slate-500">{CAMPAIGN_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CampaignManagementPanel campaigns={DEMO_CAMPAIGNS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Campaign Builder</h2>
          <p className="mt-1 text-sm text-slate-500">{CAMPAIGN_BUILDER_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Next recommended structure</p>
              <p className="text-slate-600 dark:text-slate-400">
                SP Exact + SB Defense + SD Retargeting for Earbuds Pro launch — ₹97K/mo total budget
              </p>
              <p className="text-xs text-brand-600">Awaiting approval · 3 reviewers assigned</p>
            </div>
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Keyword Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{KEYWORD_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <KeywordIntelligenceCard groups={DEMO_KEYWORDS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Bid & Budget Engine</h2>
          <p className="mt-1 text-sm text-slate-500">{BID_BUDGET_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <BidBudgetEnginePanel features={BID_BUDGET_FEATURES} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Organic + Advertising Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{ORGANIC_PAID_DIMENSIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <OrganicPaidCorrelationPanel rows={DEMO_ORGANIC_PAID} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Optimization Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{OPTIMIZATION_RECS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIOptimizationPanel recommendations={DEMO_OPTIMIZATIONS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Forecasting</h2>
        <p className="mt-1 text-sm text-slate-500">{FORECAST_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ForecastingPanel forecasts={DEMO_FORECASTS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Competitor Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{COMPETITOR_DIMENSIONS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Competitor X</span> — 34% keyword overlap · est. ₹2.1L/mo ad spend</p>
              <p><span className="font-medium">Competitor Y</span> — 28% keyword overlap · est. ₹1.4L/mo ad spend</p>
              <p className="text-xs text-slate-500">Visibility gap: 12 keywords where you rank page 2+ but competitor ranks top 5</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Experimentation</h2>
          <p className="mt-1 text-sm text-slate-500">{EXPERIMENT_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Active: SB lifestyle vs product-only hero</p>
              <p className="text-slate-600 dark:text-slate-400">Expected ROI +18% · Day 4 of 14 · 94% statistical power projected</p>
            </div>
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Learning</h2>
          <p className="mt-2 text-xs text-slate-500">{AI_LEARNING_SOURCES.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
